import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Text, Flex, Badge, Heading,
  Card, CardBody, CardHeader, useColorModeValue,
  HStack, VStack, Spinner, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalFooter, ModalBody, ModalCloseButton, useDisclosure
} from "@chakra-ui/react";
import { MdRefresh } from "react-icons/md";
import { MdOutlineStorefront } from "react-icons/md";
 
import apiService from '../services/apiService';
 
// ── Widths ────────────────────────────────────────────────────────────────────
const CLIENT_W = 200;
const COL_W    = 210;
const ROW_H    = 24;
 
// ── Date sub-row config ───────────────────────────────────────────────────────
const DATE_ROWS = [
  { key: "etl_api_hit_date",  label: "ETL API Hit Date",  color: "#2b6cb0" },
  { key: "etl_last_hit_date", label: "ETL Last Hit Date",  color: "#276749" },
  { key: "max_u_ts",          label: "Max UTC TS",  color: "#744210" },
];
 
// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = d => (!d || d === "" ? "—" : d);
 
const utcToLocal = utcStr => {
  if (!utcStr) return "—";
  try {
    const clean = utcStr.trim().replace(" ", "T").split(".")[0] + "Z";
    const dt = new Date(clean);
    if (isNaN(dt)) return utcStr;
    return dt.toLocaleString("en-US", {
      month: "short", day: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
    });
  } catch { return utcStr; }
};
 
// ── Main ──────────────────────────────────────────────────────────────────────
export default function MarketDataResults({ userProfile, appConfig, authServiceReady }) {
  const [resultData,   setResultData]   = useState({});
  const [updatedAtUtc, setUpdatedAtUtc] = useState(null);
  const [updatedBy,    setUpdatedBy]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [reloading,    setReloading]    = useState(false);
  const [error,        setError]        = useState(null);
 
  const { isOpen, onOpen, onClose } = useDisclosure();
 
  // ── Theme ──────────────────────────────────────────────────────────────────
  const bg       = useColorModeValue("white",   "gray.800");
  const border   = useColorModeValue("#e2e8f0", "#2d3748");
  const headClr  = useColorModeValue("#1a202c", "white");
  const subClr   = useColorModeValue("#4a5568", "#cbd5e0");
  const mutedClr = useColorModeValue("#718096", "#a0aec0");
  const rowEven  = useColorModeValue("#ffffff", "#1a202c");
  const rowOdd   = useColorModeValue("#f7fafc", "#171923");
  const lblBg    = useColorModeValue("#edf2f7", "#2d3748");
  const hdrBg    = "#1a3a6b";
  const subHdrBg = "#22508a";
  const thinB    = `1px solid ${border}`;
  const thickB   = "2px solid #94a3b8";
 
  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiService.getMarketDataResult();
      if (res.success && res.data) {
        setResultData(res.data.resultData  || {});
        setUpdatedAtUtc(res.data.updatedAtUtc || null);
        setUpdatedBy(res.data.updatedBy    || null);
      } else {
        setError(res.message || "Failed to load market data results");
      }
    } catch {
      setError("Failed to load market data results");
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => { fetchData(); }, [fetchData]);
 
  // ── Reload ─────────────────────────────────────────────────────────────────
  const handleReloadConfirm = async () => {
    onClose();
    setReloading(true);
    try {
      await apiService.reloadMarketDataResult(userProfile?.email);
      await fetchData();
    } catch {
      setError("Failed to reload market data results");
    } finally {
      setReloading(false);
    }
  };
 
  // ── Derive columns ─────────────────────────────────────────────────────────
  const columns = useMemo(() => {
    const seen = new Set();
    Object.values(resultData).forEach(tables => {
      Object.keys(tables).forEach(t => seen.add(t));
    });
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [resultData]);
 
  const clients = useMemo(() => Object.keys(resultData).sort(), [resultData]);
 
  // ── Sticky style factories ─────────────────────────────────────────────────
  const sHead = (left, w, extra = {}) => ({
    position: "sticky", left, top: 0, zIndex: 12,
    background: hdrBg, color: "white",
    width: w, minWidth: w, maxWidth: w,
    padding: "9px 10px", fontSize: "11px", fontWeight: "700",
    whiteSpace: "nowrap", textAlign: "center",
    borderRight: thinB, boxSizing: "border-box", ...extra,
  });
 
  const sCell = (left, w, rb, extra = {}) => ({
    position: "sticky", left, zIndex: 4,
    background: rb, width: w, minWidth: w,
    padding: 0, borderRight: thickB,
    boxSizing: "border-box", verticalAlign: "top", ...extra,
  });
 
  const dHead = {
    position: "sticky", top: 0, zIndex: 6,
    background: subHdrBg, color: "white",
    padding: "9px 10px", fontSize: "11px", fontWeight: "600",
    textAlign: "center", whiteSpace: "nowrap",
    borderRight: thinB, boxSizing: "border-box",
    width: COL_W, minWidth: COL_W,
  };
 
  const hasData = clients.length > 0;
 
  return (
    <Box p={4}>
      <VStack spacing={5} align="stretch">
 
        {/* ── Header ────────────────────────────────────────────────────── */}
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={3}>
          <Box>
            <Heading size="lg" color={headClr}>Market Data ETL Monitor</Heading>
            <Text color={subClr} mt={1} fontSize="sm">
              Latest ETL run timestamps per client and table
            </Text>
 
            {(updatedAtUtc || updatedBy) && (
              <HStack spacing={3} mt={2} wrap="wrap">
                {updatedAtUtc && (
                  <HStack spacing={1}>
                    <Text fontSize="xs" color={mutedClr}>Last updated:</Text>
                    <Text fontSize="xs" fontWeight="600" color={subClr}>
                      {utcToLocal(updatedAtUtc)}
                    </Text>
                  </HStack>
                )}
                {updatedBy && (
                  <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2} py={0.5}>
                    {updatedBy}
                  </Badge>
                )}
              </HStack>
            )}
          </Box>
 
          <HStack spacing={2} wrap="wrap" align="center">
            <Button
              leftIcon={<MdRefresh />}
              onClick={fetchData}
              variant="outline"
              size="sm"
              isLoading={loading}
            >
              Refresh
            </Button>
            <Button
              leftIcon={<MdRefresh />}
              onClick={onOpen}
              colorScheme="orange"
              size="sm"
              isLoading={reloading}
              loadingText="Reloading..."
            >
              Reload Results
            </Button>
          </HStack>
        </Flex>
 
        {/* ── Confirmation Modal ────────────────────────────────────────── */}
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontSize="md">Reload Market Data Results</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color={subClr}>
                This will trigger a fresh ETL data fetch from the market data server
                and overwrite the current results. Are you sure you want to continue?
              </Text>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
              <Button size="sm" colorScheme="orange" onClick={handleReloadConfirm}>
                Yes, Reload
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
 
        {/* ── Reloading indicator ───────────────────────────────────────── */}
        {reloading && (
          <Flex justify="center" py={4}>
            <HStack spacing={3}>
              <Spinner size="sm" color="orange.400" />
              <Text fontSize="sm" color="orange.400" fontWeight="600">
                Reloading market data from server...
              </Text>
            </HStack>
          </Flex>
        )}
 
        {/* ── Loading ───────────────────────────────────────────────────── */}
        {loading && (
          <Flex justify="center" py={16}>
            <VStack spacing={4}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text color={subClr}>Loading market data results...</Text>
            </VStack>
          </Flex>
        )}
 
        {/* ── Error ─────────────────────────────────────────────────────── */}
        {!loading && error && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color="red.400"><MdOutlineStorefront size={40} /></Box>
              <Text color="red.500" fontWeight="semibold">{error}</Text>
              <Button size="sm" onClick={fetchData} colorScheme="blue" variant="outline">Retry</Button>
            </VStack>
          </Flex>
        )}
 
        {/* ── Empty ─────────────────────────────────────────────────────── */}
        {!loading && !error && !hasData && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Box color={mutedClr}><MdOutlineStorefront size={44} /></Box>
              <Text color={mutedClr} fontSize="lg">No market data results found</Text>
            </VStack>
          </Flex>
        )}
 
        {/* ── Table ─────────────────────────────────────────────────────── */}
        {!loading && !error && hasData && (
          <Card bg={bg} borderColor={border} borderWidth="1px" overflow="hidden">
            <CardHeader pb={2} pt={4} px={4}>
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Box color="blue.500"><MdOutlineStorefront size={20} /></Box>
                  <Heading size="md" color={headClr}>ETL Run Results</Heading>
                </HStack>
 
                {/* ── Legend ─────────────────────────────────────────── */}
                <HStack spacing={3}>
                  {DATE_ROWS.map(r => (
                    <HStack key={r.key} spacing={1}>
                      <Box w="8px" h="8px" borderRadius="2px" bg={r.color} />
                      <Text fontSize="10px" color={mutedClr} fontWeight="600">{r.label}</Text>
                    </HStack>
                  ))}
                </HStack>
              </Flex>
            </CardHeader>
 
            <CardBody p={0}>
              <Box
                overflowX="auto" overflowY="auto" maxH="72vh"
                sx={{
                  "&::-webkit-scrollbar": { h: "8px", w: "8px" },
                  "&::-webkit-scrollbar-thumb": { bg: "gray.300", borderRadius: "4px" },
                }}
              >
                <table style={{
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                  minWidth: `${CLIENT_W + columns.length * COL_W}px`,
                }}>
                  <thead>
                    <tr>
                      {/* Fixed: Client Name */}
                      <th style={sHead(0, CLIENT_W, { textAlign: "left", borderRight: thickB })}>
                        Client Name
                      </th>
                      {/* Dynamic columns */}
                      {columns.map((col, i) => (
                        <th key={col} style={{ ...dHead, borderLeft: i === 0 ? thickB : thinB }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
 
                  <tbody>
                    {clients.map((clientName, idx) => {
                      const rb     = idx % 2 === 0 ? rowEven : rowOdd;
                      const rowBot = "2px solid #94a3b8";
                      const tables = resultData[clientName] || {};
 
                      return (
                        <tr key={clientName} style={{ borderBottom: rowBot }}>
 
                          {/* ── Fixed: Client Name + row labels ──────── */}
                          <td style={sCell(0, CLIENT_W, rb, { borderBottom: rowBot })}>
                            {/* Client name header */}
                            <div style={{
                              padding: "6px 12px 4px",
                              fontWeight: 700, fontSize: 13, color: headClr,
                              borderBottom: thinB,
                              minHeight: 27,
                            }}>
                              {clientName}
                            </div>
                            {/* Sub-row labels */}
                            {DATE_ROWS.map((r, ri) => (
                              <div key={r.key} style={{
                                height: ROW_H,
                                padding: "3px 12px",
                                display: "flex", alignItems: "center",
                                background: lblBg,
                                borderBottom: ri < DATE_ROWS.length - 1 ? thinB : "none",
                              }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 700,
                                  color: r.color,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}>
                                  {r.label}
                                </span>
                              </div>
                            ))}
                          </td>
 
                          {/* ── Dynamic: 3 date sub-rows per column ───── */}
                          {columns.map((col, i) => {
                            const cell = tables[col];
                            return (
                              <td key={col} style={{
                                width: COL_W, minWidth: COL_W,
                                background: rb,
                                borderLeft: i === 0 ? thickB : thinB,
                                borderBottom: rowBot,
                                boxSizing: "border-box",
                                verticalAlign: "top",
                                padding: 0,
                              }}>
                                {/* Spacer matching client name header height */}
                                <div style={{
                                  padding: "6px 10px 4px",
                                  borderBottom: thinB,
                                  minHeight: 27,
                                }} />
 
                                {/* 3 date values */}
                                {DATE_ROWS.map((r, ri) => {
                                  const val = cell?.[r.key];
                                  return (
                                    <div key={r.key} style={{
                                      height: ROW_H,
                                      padding: "3px 10px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderBottom: ri < DATE_ROWS.length - 1 ? thinB : "none",
                                    }}>
                                      {val
                                        ? <span style={{
                                            fontSize: 11,
                                            color: r.color,
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                          }}>
                                            {fmtDate(val)}
                                          </span>
                                        : <span style={{ fontSize: 13, color: mutedClr }}>—</span>
                                      }
                                    </div>
                                  );
                                })}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </CardBody>
          </Card>
        )}
 
      </VStack>
    </Box>
  );
}